import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";
import { Tag } from "clutch/src/Tag/Tag.js";
import { TextArea } from "clutch/src/TextArea/TextArea.js";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP, IS_DEV, IS_NODE } from "@/__main__/constants.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import { setVolatileKV } from "@/app/actions.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { APP_SCROLLER, appURLs } from "@/app/constants.mjs";
import {
  AdsColumn,
  Container,
  DevDisplayAdContainer,
  DisplayAdBackdrop,
  DisplayAdBase,
  DisplayAdCentered,
  DisplayAdLeaderboard,
  DisplayAdRectangle,
  DisplayAdTall,
  FULL_HEIGHT_ADS,
  InnerContainer,
} from "@/feature-ads/AdWrapper.style.jsx";
import {
  AD_HEIGHT,
  AD_VIEW_THRESHOLD,
  DISPLAY_AD_CLASS,
  DISPLAY_AD_Z_INDEX,
  SLOT_UNFILLED_CLASS,
} from "@/feature-ads/constants/constants.mjs";
import { EVENT_TRACK_AD_REPORT } from "@/feature-ads/constants/events.mjs";
import Slot from "@/feature-ads/constants/slot-symbols.mjs";
import adsRefs from "@/feature-ads/refs.mjs";
import CloseIcon from "@/inline-assets/close-icon.svg";
import type { FullModalRefOptions } from "@/shared/FullModal.js";
import FullModal from "@/shared/FullModal.jsx";
import { classNames } from "@/util/class-names.mjs";
import { devError, devLog } from "@/util/dev.mjs";
import { CONTAINER_ID, OBSERVE_CLASS } from "@/util/exit-transitions.mjs";
import globals from "@/util/global-whitelist.mjs";
import { lastOf } from "@/util/helpers.mjs";
import nextFrame from "@/util/next-frame.mjs";
import { useRoute } from "@/util/router-hooks.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";
import { watchElementVisiblity } from "@/util/use-is-visible.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const MAX_ADS = 3;
const MIN_ADS = 1;

export const ADS_COLUMN_CLASS = "aside-content-column";

// DO NOT ENABLE THIS WITHOUT UNDERSTANDING THE IMPLICATIONS OF A POTENTIAL
// CONFIRM CLICK ON OUR AD NETWORK
const SHOULD_TRY_CLICK_URL = false;

const contentRoot = () => globals.document.getElementById(CONTAINER_ID);

const adUnitWithReportButton = () => css`
  .report-btn {
    width: fit-content;
    cursor: pointer;

    &[disabled] {
      cursor: wait;
      pointer-events: none;
      opacity: 0.5;
    }
  }

  &:not(:has(iframe)) .report-btn {
    display: none;
  }

  .ltr {
    direction: ltr;
  }

  .screenshot {
    flex: 1;
    max-height: 60vh;
    max-width: min(300px, 50vw);
    margin-inline: auto;
  }

  .click-url {
    font-size: var(--sp-2_5);
    word-break: break-all;
    overflow: hidden;
    height: 1lh;
  }

  .report-form-layout {
    display: flex;
    gap: var(--sp-4);
  }
`;

const reportUi = () => css`
  & {
    background: var(--shade7);
    color: var(--shade1);
    max-width: calc(var(--sp-container) / 1);
    border-radius: var(--br);

    margin: var(--sp-2);
    padding: var(--sp-6);
    flex-direction: row;
    display: flex;
    gap: var(--sp-4);
  }

  .report-form-left {
    flex: 1;
  }

  .report-form-right {
    flex: 2;
  }

  .report-items {
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }
`;

export function DisplayAd({
  id,
  viewable = false,
  placeholder,
  wrapper: AdContainer = IS_DEV ? DevPercentDisplayAd : "div",
  children = null,
  className = "",
  containerClassName = "",
}) {
  const ref = useRef(null);
  const { t } = useTranslation();

  const reportedData = useRef(null);
  const [didInitiateReport, setDidInitiateReport] = useState(false);
  const [coreData, setCoreData] = useState(null);
  const modalRef = useRef<FullModalRefOptions>(null);

  // Initialize as unfilled
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.parentElement.classList.add(SLOT_UNFILLED_CLASS);
  }, []);

  return (
    <div {...classNames(adUnitWithReportButton(), containerClassName)}>
      <Tag
        size="xs"
        text={t("common:reportAd", "Report Ad")}
        color={"var(--shade2)"}
        className={"report-btn"}
        disabled={didInitiateReport}
        onClick={() => {
          setDidInitiateReport(true);

          (async () => {
            try {
              const data = globalThis.googletag // eslint-disable-line no-restricted-properties
                ?.pubads()
                .getSlots()
                .find((slot) => slot.getSlotElementId() === id)?.[
                Slot.lastResponse
              ];

              if (!data) {
                devError("No ad data found for slot", id);
              }

              reportedData.current = data || {};

              const elementRect = ref.current.getBoundingClientRect();

              const imageData =
                IS_APP &&
                (await blitzMessage(EVENTS.SCREENSHOT, {
                  x: Math.floor(
                    globals.devicePixelRatio * (elementRect.x - 10),
                  ),
                  y: Math.floor(
                    globals.devicePixelRatio * (elementRect.y - 10),
                  ),
                  width: Math.floor(
                    globals.devicePixelRatio * (elementRect.width + 20),
                  ),
                  height: Math.floor(
                    globals.devicePixelRatio * (elementRect.height + 20),
                  ),
                }));

              if (imageData) {
                setCoreData((prev) => ({
                  ...prev,
                  imageData,
                }));
              }

              const clickData =
                SHOULD_TRY_CLICK_URL &&
                IS_APP &&
                (await blitzMessage(EVENTS.GET_CLICK_URL, {
                  x: Math.ceil(globals.devicePixelRatio * elementRect.x),
                  y: Math.ceil(globals.devicePixelRatio * elementRect.y),
                  width: Math.floor(
                    globals.devicePixelRatio * elementRect.width,
                  ),
                  height: Math.floor(
                    globals.devicePixelRatio * elementRect.height,
                  ),
                }));

              if (clickData) {
                setCoreData((prev) => ({
                  ...prev,
                  clickData,
                }));
              }
            } catch (e) {
              devError("Error getting ad data", e);
            }

            // note modal has to be opened AFTER core calls since both depend on the
            // ad being in view and not hidden behind a modal
            modalRef.current.showModal();
          })();
        }}
      >
        {t("common:reportAd", "Report Ad")}
      </Tag>
      <AdContainer {...classNames(DisplayAdBase(), className)}>
        {placeholder && <div className="placeholder">{placeholder}</div>}
        <div
          ref={ref}
          id={id}
          {...classNames(DISPLAY_AD_CLASS, viewable && "viewable")}
        />
        {children}

        <FullModal
          ref={modalRef}
          className={"ltr"}
          onModalShow={() => setDidInitiateReport(true)}
          onModalClose={() => setDidInitiateReport(false)}
        >
          <div className={reportUi()}>
            {coreData?.imageData && (
              <div className="report-form-left">
                <img
                  className="screenshot"
                  src={coreData?.imageData}
                  alt={t("common:adScreenshot", "Ad Screenshot")}
                />
              </div>
            )}
            <div className="report-form-right report-items">
              <div className="type-h6">{t("common:reportAd", "Report Ad")}</div>
              <div className="type-body2-form">
                {t(
                  "common:reportAdDesc",
                  "Your feedback helps us improve the ads you see. Whether it was disruptive, irrelevant, or just not working right, we want to hear from you. Share the details so we can make sure your experience gets better.",
                )}
              </div>
              {coreData?.clickData && (
                <div className="click-url">
                  {t("common:clickUrl", "Click URL")}: {coreData.clickData}
                </div>
              )}
              <TextArea
                placeholder={t("common:tellUsMore", "Tell us more")}
                onChange={(e) => {
                  reportedData.current.feedback = e.target.value;
                }}
              ></TextArea>
              <Button
                onClick={() => {
                  // do report
                  if (coreData)
                    Object.assign(reportedData.current, {
                      imageData: coreData.imageData,
                      // give more data about why clickData is unavailable
                      clickData: SHOULD_TRY_CLICK_URL
                        ? coreData.clickData || "unavailable"
                        : "skipped",
                    });
                  eventBus.emit(EVENT_TRACK_AD_REPORT, reportedData.current);
                  devLog("Reported ad", reportedData.current);

                  // reset state
                  reportedData.current = null;
                  setCoreData(null);
                  setDidInitiateReport(false);
                  modalRef.current.close();

                  // remove ad from view
                  const shownAdFrame = globals.document
                    .getElementById(id)
                    ?.querySelector("iframe");
                  shownAdFrame?.remove();
                }}
              >
                {t("common:submit", "Submit")}
              </Button>
            </div>
          </div>
        </FullModal>
      </AdContainer>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useShouldShowAdhesionBanner = () => {
  const {
    remoteConfig,
    showHeaderBanner,
    showInFeedBanner,
    showInContentBanner,
  } = useSnapshot(adsRefs);
  const route = useRoute((a, b) => a?.currentPath === b?.currentPath, {
    isGlobal: true,
  });

  const [show, setShow] = useState(false);

  const canShowBanner = useMemo(() => {
    const matchedInfeed = adsRefs.INFEED_BANNER_PATHS.some((r) =>
      (route?.currentPath ?? "").includes(r),
    );

    if (showInFeedBanner && matchedInfeed) return false;

    const matchedInContent = adsRefs.IN_CONTENT_BANNER_PATHS.some((r) =>
      (route?.currentPath ?? "").includes(r),
    );

    if (showInContentBanner && matchedInContent) return false;

    const matchedHeaderRoute = adsRefs.EXCLUDED_HEADER_BANNER_PATHS.some((r) =>
      (route?.currentPath ?? "").includes(r),
    );

    if (showHeaderBanner && !matchedHeaderRoute) return false;

    const matchedRoute = adsRefs.ADHESION_PATHS.some((r) =>
      (route?.currentPath ?? "").startsWith(r),
    );
    // return matchedRoute
    return matchedRoute && remoteConfig.adhesion;
  }, [
    showInFeedBanner,
    showInContentBanner,
    remoteConfig.adhesion,
    route?.currentPath,
  ]);

  // when a route and country are matched or none of the other banners are shown, show the anchor banner
  useLayoutEffect(() => {
    setShow(canShowBanner);
  }, [show, canShowBanner]);

  return [show, () => setShow(false)] as const;
};

function AdhesionBannerContainer() {
  const [shouldShowAdhesionBanner, onUserClose] = useShouldShowAdhesionBanner();

  useLayoutEffect(() => {
    setVolatileKV("shouldOmitBottomContent", shouldShowAdhesionBanner);
    return () => {
      setVolatileKV("shouldOmitBottomContent", false);
    };
  }, [shouldShowAdhesionBanner]);

  if (!shouldShowAdhesionBanner) return null;

  return (
    <DisplayAd
      placeholder={""}
      id={"display-desktop-anchor"}
      className={DisplayAdLeaderboard()}
    >
      <button className="close-btn" onClick={onUserClose}>
        <CloseIcon />
      </button>
    </DisplayAd>
  );
}

const useShouldShowHeaderBanner = () => {
  const {
    remoteConfig,
    showHeaderBanner,
    showInContentBanner,
    shouldShowAds: shouldShowAdsHandlers,
  } = useSnapshot(adsRefs);
  const route = useRoute((a, b) => a?.currentPath === b?.currentPath, {
    isGlobal: true,
  });

  const canShowBanner = useMemo(() => {
    const shouldShowAds = shouldShowAdsHandlers.every((fn) => fn());
    if (!shouldShowAds || !showHeaderBanner) return false;

    const matchedInContent = adsRefs.IN_CONTENT_BANNER_PATHS.some((r) =>
      (route?.currentPath ?? "").includes(r),
    );
    if (matchedInContent && showInContentBanner) return false;

    const matchedHeaderRoute = adsRefs.EXCLUDED_HEADER_BANNER_PATHS.some((r) =>
      (route?.currentPath ?? "").includes(r),
    );

    // ensure we don't display banner on excluded paths
    return !matchedHeaderRoute && remoteConfig.adhesion;
  }, [
    shouldShowAdsHandlers,
    showHeaderBanner,
    showInContentBanner,
    remoteConfig.adhesion,
    route?.currentPath,
  ]);

  return [canShowBanner] as const;
};

const AdHeaderBannerDisplayAd = (): React.ReactNode => {
  const [shouldShowHeaderBanner] = useShouldShowHeaderBanner();

  if (!shouldShowHeaderBanner) return null;

  const msiImage = `${appURLs.CDN}/infeed-images/msi-728x90.webp`;
  const msiLink = `${appURLs.MSI}?utm_source=blitz-app&utm_medium=ad&utm_campaign=blitz-fill-ad`;

  return (
    <DisplayAd
      placeholder={<AdPlaceholder img={msiImage} link={msiLink} />}
      id={"display-desktop-anchor-infeed"}
      {...classNames(DisplayAdLeaderboard(), "top-banner")}
      containerClassName={DisplayAdCentered()}
    />
  );
};

function AdWrapper({ children }) {
  const y = useRef(0);
  const [adCount, setAdCount] = useState(MAX_ADS);

  const {
    volatile: {
      "ads-left-rail": showLeftRail = false,
      "ads-skin": showSkin = false,
    },
  } = useSnapshot(readState);
  const showRr3 = !showLeftRail;

  const { shouldShowAds: shouldShowAdsHandlers } = useSnapshot(adsRefs);

  const [isUsingAdblock, _setIsUsingAdblock] = useState(false);

  // Every handler needs to be called before we can resolve, as some may be using hooks.
  const shouldUseContainer = shouldShowAdsHandlers.every((fn) => fn());

  const shouldShowAds = !isUsingAdblock && shouldUseContainer;

  const updateMaxAds = () => {
    if (!colRefs.size) return;
    const content = globals.document.querySelector<HTMLElement>(
      `#${CONTAINER_ID}`,
    );
    const offset = content ? content.offsetTop : 0;
    const height = FULL_HEIGHT_ADS
      ? globals.innerHeight
      : globals.innerHeight - offset - y.current;

    setAdCount((prev) => {
      const newCount = getMaxAds(height);
      if (prev === newCount) return prev;
      setVolatileKV("colAdsCount", newCount);
      return newCount;
    });
    for (const col of colRefs) noAnimation(col);
  };

  // align the ad column to the start of page content
  const [shouldAlignContent, setShouldAlignContent] = useState(false);

  const colRefs = new Set<HTMLElement>();
  const colRef = (node) => {
    if (!node) return;
    colRefs.add(node);
  };

  // detect adblockers
  useLayoutEffect(() => {
    if (!colRefs.size) return;

    const cbs = [];

    for (const col of colRefs) {
      cbs.push(
        watchElementVisiblity(col, (_visible) => {
          // if (visible) return;
          // We could probably emit an event for this for funsies
          // FIXME: this logic is incorrect, due to the comment below
          // if (IS_DEV) {
          //   devError("[ads] adblock detected");
          // }
          // FIXME
          /* this also triggers when ad visibility changes due to screen size
          so now when one rail is hidden, ads are disabled entirely for
          the entire session
          a more classical approach would be to check for the blocking of
          a script named "ads.js" or something and disabling the entire ads
          feature if that is the case
          */
          // setIsUsingAdblock(true);
        }),
      );
    }

    return () => cbs.forEach((cb) => cb());
  });

  useLayoutEffect(() => {
    if (!colRefs.size) return;

    // prevent all transitions on first render
    for (const col of colRefs) {
      noAnimation(col);
      nextFrame(() => allowAnimation(col));
    }

    const setYOffset = (value: number, instant = false) => {
      if (!shouldAlignContent) {
        for (const col of colRefs) {
          col.style.setProperty("--content-start", "");
          col.style.setProperty("--sticky-header-height", "");
        }
        return;
      }
      y.current = value;
      for (const col of colRefs) {
        col.style.setProperty("--content-start", `${value}px`);
        if (!instant) allowAnimation(col);
      }
      // update the sticky header height
      // we do not have to do it in this setter BUT we do NOT want to make this
      // reactive on header height in volatile state as it will cause display flicker
      // while JS is catching up; we always want to set this to the max stuck height.
      const headerHeight = getHeaderMaxHeight();
      for (const col of colRefs) {
        col.style.setProperty("--sticky-header-height", `${headerHeight}px`);
      }
    };

    // the ad to the sidebar-align element
    const content = contentRoot();

    const alignElement =
      content &&
      (findAlignElement(content) ||
        content.querySelector<HTMLElement>(`.${OBSERVE_CLASS}`));
    if (alignElement) setYOffset(getOffset(alignElement), true);

    const alignSizeObs = new ResizeObserver(([entry]) => {
      if (!(entry.target instanceof HTMLElement)) return;
      setYOffset(getOffset(entry.target));
      updateMaxAds();
    });
    if (alignElement) alignSizeObs.observe(alignElement);

    const setAlignElement = (el: HTMLElement) => {
      alignSizeObs.disconnect();
      alignSizeObs.observe(el);
      // trigger this early to re-render immediately
      setYOffset(getOffset(el));
      // add align class so we can watch for when this node is removed
      el.classList.add("sidebar-align");
    };
    // find new align elements in page content
    const alignObs = new MutationObserver((mutations) => {
      for (const { addedNodes, removedNodes } of mutations) {
        for (const node of addedNodes) {
          if (!(node instanceof HTMLElement) || node.inert) continue;
          const alignElement = findAlignElement(node);
          if (alignElement) setAlignElement(alignElement);
        }
        for (const node of removedNodes) {
          if (!(node instanceof Element && findAlignElement(node))) continue;
          const newAlignment = findAlignElement(content);
          if (newAlignment) setAlignElement(newAlignment);
        }
      }
    });
    alignObs.observe(content, {
      childList: true,
      subtree: true,
    });

    const viewabilityObs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const method = entry.isIntersecting ? "add" : "remove";
          entry.target.classList[method]("viewable");
        }
      },
      { threshold: AD_VIEW_THRESHOLD },
    );
    for (const col of colRefs) {
      for (const ad of col.getElementsByClassName(DISPLAY_AD_CLASS)) {
        viewabilityObs.observe(ad);
      }
    }
    return () => {
      alignObs.disconnect();
      alignSizeObs.disconnect();
      viewabilityObs.disconnect();
    };
  }, [shouldShowAds, shouldAlignContent]);

  // Check if the whole ads column has enough leeway to align vertically.
  useLayoutEffect(() => {
    if (IS_NODE) return;

    function resizeListener() {
      const adsElement = globals.document.querySelector(`.${ADS_COLUMN_CLASS}`);
      if (!adsElement) {
        return;
      }
      const rootElement = contentRoot();
      const top = 0;
      // const { top } = rootElement.getBoundingClientRect();
      const { height } = adsElement.getBoundingClientRect();
      const alignElement = findAlignElement(rootElement);
      if (!alignElement) {
        return;
      }
      const { top: alignTop } = alignElement.getBoundingClientRect();
      const [scroller] = globals.document.getElementsByClassName(APP_SCROLLER);
      const value =
        alignTop + top + height + scroller.scrollTop < globals.innerHeight;

      setShouldAlignContent(value);
    }

    nextFrame(resizeListener);

    globals.addEventListener("resize", resizeListener);

    return () => {
      globals.removeEventListener("resize", resizeListener);
    };
  }, []);

  // right nav should have alternate view when expanding will obscure ads
  useLayoutEffect(() => {
    const [root] = globals.document.getElementsByClassName(APP_SCROLLER);

    const observersCallback = () => {
      updateMaxAds();
    };

    const resizeObserver = new ResizeObserver(observersCallback);
    resizeObserver.observe(root);

    return () => resizeObserver.disconnect();
  }, []);

  const { t } = useTranslation();

  if (!shouldUseContainer) {
    return children;
  }

  return (
    <>
      {shouldShowAds && showSkin && (
        <DisplayAd id={"display-skin"} placeholder={""} />
      )}
      <Container>
        {shouldShowAds && !showSkin && (
          <>
            {showLeftRail && (
              <div
                style={{
                  "--count": adCount,
                  zIndex: DISPLAY_AD_Z_INDEX,
                  order: -1,
                }}
                {...classNames(ADS_COLUMN_CLASS, AdsColumn(), "left-col")}
                ref={colRef}
              >
                <DisplayAd
                  placeholder={<AdPlaceholder prefix="tall" />}
                  id={"display-lr-1"}
                  className={DisplayAdTall()}
                />
                <div
                  className={DisplayAdBackdrop()}
                  data-label={"Advertisements"}
                >
                  <a href={"/pro"} className="ad-removal-promo">
                    {t("common:removeAds", "Remove Ads")}
                  </a>
                </div>
              </div>
            )}
            <div
              style={{
                "--count": adCount,
                zIndex: DISPLAY_AD_Z_INDEX,
                order: 2,
              }}
              {...classNames(ADS_COLUMN_CLASS, AdsColumn())}
              ref={colRef}
            >
              <DisplayAd
                placeholder={<AdPlaceholder />}
                id={"display-rr-1"}
                className={DisplayAdRectangle()}
              />
              <DisplayAd
                placeholder={<AdPlaceholder />}
                id={"display-rr-2"}
                className={DisplayAdRectangle()}
              />
              {showRr3 && (
                <DisplayAd
                  placeholder={<AdPlaceholder />}
                  id={"display-rr-3"}
                  className={DisplayAdRectangle()}
                />
              )}
              {/* Label must be "Advertisements" or "Sponsored Links". No other variations are allowed at this time.
              See https://support.google.com/adsense/answer/4533986?hl=en */}
              <div
                className={DisplayAdBackdrop()}
                data-label={"Advertisements"}
              >
                <a href={"/premium"} className="ad-removal-promo">
                  {t("common:removeAds", "Remove Ads")}
                </a>
              </div>
            </div>
          </>
        )}

        <InnerContainer className="inner-wrapper-col">
          <AdHeaderBannerDisplayAd />
          {children}
        </InnerContainer>
      </Container>
      {IS_APP && <AdhesionBannerContainer />}
    </>
  );
}

function DevPercentDisplayAd({ children, ...props }) {
  const ref = useCallbackRef((node: HTMLDivElement) => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        entry.target.childNodes.forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          el.innerHTML = (entry.intersectionRatio * 100).toFixed(3) + "%";
          const UPPER = 0.56;
          const LOWER = 0.51;
          const range = UPPER - LOWER;
          const percent = (entry.intersectionRatio - LOWER) / range;
          el.style.color =
            entry.intersectionRatio < UPPER
              ? entry.intersectionRatio > LOWER
                ? `rgb(${(1 - percent) * 255}, 255, 0)`
                : "red"
              : "";
        });
      },
      { threshold: Array.from({ length: 1000 }, (_, i) => i / 1000) },
    );
    obs.observe(node);

    return () => obs.disconnect();
  });

  return (
    <div
      {...props}
      {...classNames(props.className, DisplayAdBase(), DevDisplayAdContainer())}
    >
      {children}
      <div ref={ref} className="dev percents">
        <div />
        <div />
        <div />
        <div />
      </div>
      <div className="dev center-hints" />
    </div>
  );
}

const getMaxAds = (h: number) =>
  Math.max(MIN_ADS, Math.min(MAX_ADS, Math.floor(h / AD_HEIGHT)));
const getOffset = (
  node: HTMLElement,
  direction = node.dataset.direction || "top",
) => {
  switch (direction) {
    case "top":
      return node.offsetTop;
    case "bottom":
      return node.offsetTop + node.offsetHeight;
    case "left":
      return node.offsetLeft;
    case "right":
      return node.offsetLeft + node.offsetWidth;
  }
  return 0;
};

const findAlignElement = (node: Element): HTMLElement => {
  let alignElement = node.classList.contains("sidebar-align")
    ? node
    : // look for the LAST element with the class
      lastOf(node.getElementsByClassName("sidebar-align"));
  while (alignElement instanceof HTMLElement) {
    // in rare cases we need the sidebar align element to persist
    // the usage for this is to add sidebar-align and sidebar-align-child to signal
    // that the parent will be used as the align element, and recalculated when the
    // child is added or removed
    if (alignElement.classList.contains("sidebar-align-child")) {
      alignElement = alignElement.parentElement;
      continue;
    }
    const sticky =
      getComputedStyle(alignElement).position === "sticky" || // css sticky
      alignElement.dataset.sticky === "true"; // data-sticky
    if (!sticky) break;
    alignElement = alignElement.nextElementSibling;
  }
  return alignElement instanceof HTMLElement ? alignElement : null;
};

const getHeaderMaxHeight = () => {
  const stickies = globals.document
    .querySelector<HTMLElement>(
      // must select inside observe class because page header
      // may be in transition
      `.${OBSERVE_CLASS} .page-header`,
    )
    ?.parentNode.querySelectorAll<HTMLElement>(
      "[data-sticky-cumulative-height]",
    );
  const lastSticky = stickies?.[stickies.length - 1];
  return lastSticky?.dataset.stickyCumulativeHeight || 0;
};

const noAnimation = (node: HTMLElement) =>
  node.style.setProperty("--transition", "0s");
const allowAnimation = (node: HTMLElement) =>
  node.style.removeProperty("--transition");

export default AdWrapper;

const maxIndex = 6;
const prefixes = ["tall", "slot"];
export const promoImgMap = {};
for (const prefix of prefixes) {
  promoImgMap[prefix] = new Array(maxIndex).fill(null).map((_, i) => {
    return `${appURLs.CDN}/self-promotion/premium/${prefix}_${i + 1}.webp`;
  });
}

const availableIndices = new Set(
  new Array(maxIndex).fill(null).map((_, i) => i + 1),
);
const unavailableIndices = new Set();

type AdPlaceholderProps = {
  prefix?: "tall" | "slot";
  img?: string;
  link?: string;
};

export function AdPlaceholder({
  prefix = "slot",
  img,
  link,
}: AdPlaceholderProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(null);

  useLayoutEffect(() => {
    let i;
    while (availableIndices.size && !availableIndices.has(i)) {
      i = ~~(Math.random() * maxIndex) + 1;
    }
    setIndex(i);
    availableIndices.delete(i);
    unavailableIndices.add(i);
    return () => {
      availableIndices.add(i);
      unavailableIndices.delete(i);
    };
  }, []);

  if (!index) return null;

  const imgUrl = img ? img : promoImgMap[prefix][index];
  const href = link
    ? link
    : `/pro?utm_source=blitz&utm_medium=ad&utm_campaign=blitz_ad_${index}`;
  const alt = t("common:getSubscriber", "Get Premium");

  return (
    <a href={href} {...{ target: IS_APP ? undefined : "_blank" }}>
      <img src={imgUrl} alt={alt} />
    </a>
  );
}
